import { Fragment } from "react/jsx-runtime";

import { GqlMapEditorLayerStyle } from "~/__generated__";
import GaiaMapDataLayer from "~/modules/map/components/GaiaMapDataLayer";
import GaiaMapFeatureClickListener from "~/modules/map/components/GaiaMapEventListeners/GaiaMapFeatureClickListener";
import GaiaMapStyle from "~/modules/map/components/GaiaMapStyle";
import { MapEditorLayerFragment } from "~/modules/mapEditor/api/MapEditorLayer.fragment.generated";
import generateDefaultMapStyle from "~/modules/mapEditor/components/MapEditorMapView/generateDefaultMapStyle";
import generateIconMapStyle from "~/modules/mapEditor/components/MapEditorMapView/generateIconMapStyle";

type Props = {
    layer: MapEditorLayerFragment;
    selectable?: boolean;
    selectedLayerId?: string;
    selectedFeatureId?: string;
    hoveredFeatureId?: string;
    onSelect: (layerId: string, featureId: string) => void;
};

const generateMapStyle = (
    style: GqlMapEditorLayerStyle,
    layerId: string,
    isSelected: boolean,
    color?: string,
    selectedFeatureId?: string,
    hoveredFeatureId?: string,
) => {
    switch (style) {
        case GqlMapEditorLayerStyle.MapStyleDefault:
            return generateDefaultMapStyle(layerId, color, isSelected, selectedFeatureId, hoveredFeatureId);
        case GqlMapEditorLayerStyle.MapStyleIcon:
            return generateIconMapStyle(layerId, color, hoveredFeatureId);
        default:
            throw new Error(`Unsupported map style: ${style}`);
    }
};

export default function EditorMapLayers({
    layer,
    selectable = true,
    selectedLayerId,
    selectedFeatureId,
    hoveredFeatureId,
    onSelect,
}: Props) {
    if (!layer.featureCollection) return undefined;

    const collection = layer.featureCollection as GeoJSON.FeatureCollection;

    const styles = generateMapStyle(
        layer.style,
        layer.id,
        layer.id === selectedLayerId,
        layer.color ?? undefined,
        selectedFeatureId,
        hoveredFeatureId,
    );

    const onClick = (feature?: GeoJSON.Feature) => {
        if (!feature && !selectedFeatureId && selectedLayerId) {
            // TODO: solve editor modes better
            // onSelect()
        } else if (feature) {
            onSelect(layer.id, feature?.properties?.id);
        }
    };

    return (
        <Fragment key={layer.id}>
            <GaiaMapDataLayer id={layer.id} data={collection} />
            <GaiaMapStyle layers={styles} />
            {selectable && selectedLayerId && selectedLayerId === layer.id && (
                <GaiaMapFeatureClickListener layerIds={styles.map(style => style.id)} onClick={onClick} />
            )}
        </Fragment>
    );
}
